import { ErpDiv, ErpSvg, ErpH3, ErpP, ErpButtonRaw } from "@/components/erp";
import React from 'react';

export interface ErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: React.ReactNode | ((props: { error: Error | null; errorInfo: React.ErrorInfo | null; retry: () => void }) => React.ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  resetKey: number;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.props.onReset?.();
    // Bumping resetKey remounts the subtree: a deterministic render error
    // would otherwise be re-caught immediately and loop forever.
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: prev.resetKey + 1,
    }));
  };

  override render(): React.ReactNode {
    const { hasError, error, errorInfo, resetKey } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return <React.Fragment key={resetKey}>{children ?? null}</React.Fragment>;
    }

    if (typeof fallback === 'function') {
      return fallback({ error, errorInfo, retry: this.handleRetry });
    }

    if (fallback) {
      return fallback;
    }

    const isDev = import.meta.env.DEV;
    const isChunkLoadError =
      error?.name === 'ChunkLoadError' || error?.message?.includes('ChunkLoadError');

    return (
      <ErpDiv className="error-boundary-container error-boundary-fallback">
        <ErpDiv className="error-boundary-content">
          <ErpDiv className="error-boundary-icon">
            <ErpSvg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </ErpSvg>
          </ErpDiv>
          <ErpH3 className="error-boundary-title">Something went wrong</ErpH3>
          <ErpP className="error-boundary-message">{error?.message || 'An unexpected error occurred.'}</ErpP>
          {isChunkLoadError ? (
            <ErpButtonRaw type="button" className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2" onClick={() => window.location.reload()}>
              Reload Page
            </ErpButtonRaw>
          ) : (
            <ErpButtonRaw type="button" className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2" onClick={this.handleRetry}>
              Try Again
            </ErpButtonRaw>
          )}
          {isDev && error && (
            <details className="error-boundary-details">
              <summary>Error Details</summary>
              <pre>{error.stack}</pre>
              {errorInfo?.componentStack && (
                <pre>Component info: {errorInfo.componentStack}</pre>
              )}
            </details>
          )}
        </ErpDiv>
      </ErpDiv>
    );
  }
}

export default ErrorBoundary;
